// Цуврал — тайлбар, нийт явц, хэсгүүд (үзсэн ✓ / үзэж буй ▶ / эхлээгүй ○)
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Spin, message } from "antd";
import { FiCheck, FiChevronLeft, FiPlay } from "react-icons/fi";
import { instance } from "utils/axios";
import { KIND, seriesKind, fmtDur, itemPath, ProgressBar } from "./common";

function ItemRow({ it, idx, seriesId, isVideo }) {
  const p = it.progress;
  const dur = it.durationSec ? fmtDur(it.durationSec) : "";
  const to = itemPath(it.id, seriesId);

  if (p?.completed) {
    const views = p.viewCount > 1 ? ` · ${p.viewCount} удаа` : "";
    return (
      <Link to={to} className="flex items-center gap-3 px-4 py-3.5 text-gray-900 hover:text-gray-900">
        <span className="h-8 w-8 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0"><FiCheck size={16} strokeWidth={3} /></span>
        <span className="flex-1 min-w-0">
          <span className="block text-[15px] font-semibold">{it.title}</span>
          <span className="block text-xs text-gray-500">{[dur, isVideo ? "Бүтэн үзсэн" : "Уншсан"].filter(Boolean).join(" · ")}{views}</span>
        </span>
      </Link>
    );
  }
  if (p) {
    // Үзэж эхэлсэн, дуусаагүй
    return (
      <Link to={to} className="flex items-center gap-3 px-4 py-3.5 bg-[#faf7fc] text-gray-900 hover:text-gray-900">
        <span className="h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center shrink-0"><FiPlay size={14} className="fill-white ml-0.5" /></span>
        <span className="flex-1 min-w-0">
          <span className="block text-[15px] font-bold">{it.title}</span>
          <span className="block text-xs font-semibold text-primary">
            {it.durationSec ? `${fmtDur(p.lastPosition)} / ${dur}` : `${p.watchedPct || 0}%`} · үргэлжлүүлэх
          </span>
          <span className="flex mt-1.5"><ProgressBar value={p.watchedPct || 0} total={100} className="h-1" /></span>
        </span>
      </Link>
    );
  }
  return (
    <Link to={to} className="flex items-center gap-3 px-4 py-3.5 text-gray-900 hover:text-gray-900">
      <span className="h-8 w-8 rounded-full border-2 border-gray-200 text-gray-500 text-xs font-bold flex items-center justify-center shrink-0">{idx + 1}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] font-semibold">{it.title}</span>
        {dur && <span className="block text-xs text-gray-500">{dur}</span>}
      </span>
    </Link>
  );
}

function Series() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    instance({ method: "get", url: `/learning/series/${id}` })
      .then((res) => setS(res.data || null)) // 204 — харагдахгүй цуврал
      .catch(() => message.error("Мэдээлэл татаж чадсангүй"))
      .then(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-16"><Spin /></div>;
  if (!s) {
    return (
      <div className="pt-6 max-w-2xl mx-auto">
        <section className="pcard pcard-pad text-center py-10 text-gray-400 text-sm">
          Энэ сургалт олдсонгүй. <Link to="/learning" className="text-primary font-semibold">Сургалт руу буцах</Link>
        </section>
      </div>
    );
  }

  const kind = seriesKind(s);
  const isVideo = kind === "video";
  const items = s.items || [];
  // Үргэлжлүүлэх хэсэг: эхэлсэн дуусаагүй → эхлээгүй → (бүгд дууссан бол) эхнийх
  const inProgress = items.findIndex((i) => i.progress && !i.progress.completed);
  const notDone = items.findIndex((i) => !i.progress?.completed);
  const targetIdx = inProgress >= 0 ? inProgress : notDone >= 0 ? notDone : 0;
  const target = items[targetIdx];
  const btnLabel = s.done >= s.itemCount
    ? "Дахин үзэх"
    : s.started > 0 ? `${targetIdx + 1}-р хэсгийг үргэлжлүүлэх` : isVideo ? "Үзэж эхлэх" : "Унших";

  return (
    <div className="pt-2 md:pt-6 pb-6 max-w-2xl mx-auto flex flex-col gap-4">
      <header className="pcard px-3 pt-2 pb-4 md:px-5">
        <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/learning"))}
          className="inline-flex items-center gap-0.5 h-10 pr-2 text-[15px] font-semibold text-primary">
          <FiChevronLeft size={20} /> Буцах
        </button>
        <div className="px-1">
          <div className="text-[13px] font-semibold text-gray-500">{[s.category, KIND[kind]?.label].filter(Boolean).join(" · ")}</div>
          <h1 className="text-[22px] font-bold tracking-tight leading-tight mt-1">{s.title}</h1>
          {s.description && <p className="text-sm text-gray-700 leading-relaxed mt-2 whitespace-pre-line">{s.description}</p>}
          {s.itemCount > 1 && (
            <div className="flex items-center gap-2.5 mt-3.5">
              <ProgressBar value={s.done} total={s.itemCount} className="h-2" />
              <span className="text-[13px] font-bold text-gray-700">{s.done} / {s.itemCount} {isVideo ? "үзсэн" : "уншсан"}</span>
            </div>
          )}
          {target && (
            <Link to={itemPath(target.id, s.id)}
              className="mt-3.5 h-12 rounded-xl bg-primary text-white hover:text-white text-[15px] font-bold flex items-center justify-center gap-2">
              <FiPlay className="fill-white" /> {btnLabel}
            </Link>
          )}
        </div>
      </header>

      {items.length > 0 && (
        <ol className="pcard overflow-hidden divide-y divide-gray-100">
          {items.map((it, i) => (
            <li key={it.id}><ItemRow it={it} idx={i} seriesId={s.id} isVideo={it.kind === "video"} /></li>
          ))}
        </ol>
      )}
      {isVideo && (
        <p className="text-xs text-gray-500 leading-relaxed px-1">
          90%-иас дээш үзсэн бичлэг “Бүтэн үзсэн” болно. Дараа нь нээхэд зогссон газраасаа үргэлжилнэ.
        </p>
      )}
    </div>
  );
}

export default Series;
